import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  MenuItem,
} from "@mui/material";

import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { apigetPublic, apipostFormData } from "../../service/api";

// Your state list for dropdown
const stateCodes = [
  "AL","AZ","CO","FL","GA","MD","NC","SC","TN","TX","VA"
];

const validationSchema = yup.object({
  name: yup.string().required("Branch name is required"),
  slug: yup.string().required("Slug is required"),
  address: yup.string().required("Branch address is required"),
  city: yup.string().required("City is required"),
  stateCode: yup.string().required("State is required"),
  zip: yup.string().required("ZIP code is required"),
  phone: yup.string().required("Phone is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
});

export default function BranchCreateForm() {
  const [branchImageFile, setBranchImageFile] = useState(null);
  const [loanOfficers, setLoanOfficers] = useState([]);

  // Load loan officers for assignment
  useEffect(() => {
    const loadLOs = async () => {
      const result = await apigetPublic("loanofficer/search");
      if (result?.status === 200 && Array.isArray(result.data?.result)) {
        setLoanOfficers(result.data.result);
      }
    };
    loadLOs();
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      slug: "",
      address: "",
      city: "",
      stateCode: "",
      zip: "",
      phone: "",
      email: "",
      nmls: "",
      preQualificationLink: "",
      loanOfficers: [], // array of LO IDs
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();

        Object.entries(values).forEach(([key, val]) => {
          if (Array.isArray(val)) {
            val.forEach((v, i) => formData.append(`${key}[${i}]`, v));
          } else {
            formData.append(key, val);
          }
        });

        if (branchImageFile) {
          formData.append("branchImage", branchImageFile);
        }

        const result = await apipostFormData("branches", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (result?.status === 201) {
          toast.success("Branch created successfully!");
          resetForm();
          setBranchImageFile(null);
        } else {
          toast.error(result?.data?.message || "Error creating branch");
        }
      } catch (err) {
        console.error(err);
        toast.error("Unexpected error submitting form.");
      }
    },
  });

  // Auto-generate slug when name changes
  const handleNameChange = (e) => {
    formik.handleChange(e);
    const name = e.target.value;
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]/gi, "")
      .substring(0, 20);
    formik.setFieldValue("slug", slug);
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 900, mx: "auto", mt: 8 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Create New Branch
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          
          {/* Branch Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="name"
              label="Branch Name"
              value={formik.values.name}
              onChange={handleNameChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />
          </Grid>

          {/* Slug */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="slug"
              label="Slug"
              value={formik.values.slug}
              onChange={formik.handleChange}
              error={formik.touched.slug && Boolean(formik.errors.slug)}
              helperText={formik.touched.slug && formik.errors.slug}
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="address"
              label="Street Address"
              value={formik.values.address}
              onChange={formik.handleChange}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={formik.touched.address && formik.errors.address}
            />
          </Grid>

          {/* City */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              name="city"
              label="City"
              value={formik.values.city}
              onChange={formik.handleChange}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
            />
          </Grid>

          {/* State Code */}
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              name="stateCode"
              label="State"
              value={formik.values.stateCode}
              onChange={formik.handleChange}
              error={
                formik.touched.stateCode && Boolean(formik.errors.stateCode)
              }
              helperText={formik.touched.stateCode && formik.errors.stateCode}
            >
              {stateCodes.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Zip */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              name="zip"
              label="ZIP Code"
              value={formik.values.zip}
              onChange={formik.handleChange}
              error={formik.touched.zip && Boolean(formik.errors.zip)}
              helperText={formik.touched.zip && formik.errors.zip}
            />
          </Grid>

          {/* Contact */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="phone"
              label="Phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
              error={formik.touched.phone && Boolean(formik.errors.phone)}
              helperText={formik.touched.phone && formik.errors.phone}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="email"
              label="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>
  {/* NMLS */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="nmls"
              label="NMLS #"
              value={formik.values.nmls}
              onChange={formik.handleChange}
            />
          </Grid>

          {/* Pre-Qualification Link */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="preQualificationLink"
              label="Pre-Qualification Link"
              value={formik.values.preQualificationLink}
              onChange={formik.handleChange}
              helperText="URL for the Get Pre-Qualified button on the branch page"
            />
          </Grid>

          {/* Branch Image */}
          <Grid item xs={12}>
            <Typography variant="subtitle2">Upload Branch Image</Typography>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setBranchImageFile(e.target.files[0])}
            />
          </Grid>

          {/* Assign Loan Officers */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Assign Loan Officers (Optional)
            </Typography>

            <TextField
              select
              fullWidth
              name="loanOfficers"
              label="Select Loan Officers"
              SelectProps={{ multiple: true }}
              value={formik.values.loanOfficers}
              onChange={(e) =>
                formik.setFieldValue("loanOfficers", e.target.value)
              }
            >
              {loanOfficers.map((lo) => (
                <MenuItem key={lo._id} value={lo._id}>
                  {lo.firstName} {lo.lastName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Submit */}
          <Grid item xs={12}>
            <Button type="submit" variant="contained" fullWidth>
              Create Branch
            </Button>
          </Grid>

        </Grid>
      </form>
    </Paper>
  );
}
